import React, { useState } from 'react'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'

import Topbar from '@/modules/base/components/topbar/topbar'
import DataImportInput from './data-import-input/data-import-input'
import MassImportMain from '@/modules/import/mass-import/mass-import-main'

import articlesTemplate from '@assets/Muster-Artikel-Import.xlsx'
import customersTemplate from '@assets/Muster-Kunden-Import.xlsx'
import suppliersTemplate from '@assets/Muster-Lieferanten-Import.xlsx'

import IconCustomers from '@assets/images/icons/customersImport.svg'
import IconSuppliers from '@assets/images/icons/suppliersImport.svg'
import IconArticles from '@assets/images/icons/articlesImport.svg'

import { modalConfirmation } from '../_helpers/openModal'
import useCheckAccessRights from '../_helpers/accessCheck/useCheckAccessRights'
import { USER_RIGHTS } from '@/modules/base/util/constants'
import { useSelector } from 'react-redux'
import { withAsyncData } from '@/utils/withAsyncData'
import {
  IMPORT_TYPE_CUSTOMERS,
  IMPORT_TYPE_ARTICLES,
  IMPORT_TYPE_ARTICLES_MASS,
  IMPORT_TYPE_CUSTOMERS_MASS,
  IMPORT_TYPE_SUPPLIERS_MASS,
  IMPORT_TYPE_SUPPLIERS,
} from '@/modules/import/mass-import/mass-import-service'

const { massImport: texts } = lang

const VIEW_STATE = {
  EDIT: '1',
  TABLE: '2',
  SUMMARY: '3',
  FINISH: '4',
  ARTICLES_MASS: '5',
  CUSTOMERS_MASS: '6',
  SUPPLIERS_MASS: '7',
}

const useStyles = makeStyles(theme => ({
  // eslint-disable-next-line
  loader: {
    height: '100%',
    top: 85,
  },
  // eslint-disable-next-line
  importActive: {
    textAlign: 'center',
  },
  dataImportComponent: {
    width: '100%',
    flexGrow: 1,
    background: '$colorWhite',
    display: 'flex',
    flexDirection: 'column',
  },
  dataImportContent: {
    width: '100%',
    textAlign: 'center',
    overflow: 'auto',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  importSelection: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },
  // eslint-disable-next-line
  dataImportContinueButton: {
    marginLeft: '15px',
  },
  // eslint-disable-next-line
  dataImportCancelButton: {
    marginLeft: '15px',
  },
}))

const DataImportComponent = () => {
  const classes = useStyles()

  const [data, setImportData] = useState(null)
  const [fileName, setFileName] = useState('')
  const canImportCustomers = useCheckAccessRights(USER_RIGHTS.CUSTOMERS.WRITE)
  const canImportSupplier = useCheckAccessRights(USER_RIGHTS.SUPPLIERS.WRITE)
  const canImportArticles = useCheckAccessRights(USER_RIGHTS.ARTICLES.WRITE)
  const subscriptionsLoading = useSelector(state => state.subscription.details).loading

  const [viewState, setViewState] = useState(VIEW_STATE.EDIT)

  const onAbortClick = () => {
    modalConfirmation(onCancelButtonClick, {
      title: texts.abortImportModal.title,
      description: texts.abortImportModal.description,
      abortButtonLabel: texts.abortImportModal.buttons.proceed,
      confirmButtonLabel: texts.abortImportModal.buttons.abort,
    })
  }

  const onCancelButtonClick = () => {
    switch (viewState) {
      case VIEW_STATE.ARTICLES_MASS:
      case VIEW_STATE.CUSTOMERS_MASS:
      case VIEW_STATE.SUPPLIERS_MASS:
        setViewState(VIEW_STATE.EDIT)
        break
    }
  }

  const downloadArticlesTemplate = (e) => {
    e.stopPropagation()
    e.preventDefault()
    window.open(articlesTemplate)
  }

  const downloadCustomersTemplate = (e) => {
    e.stopPropagation()
    e.preventDefault()
    window.open(customersTemplate)
  }

  const downloadSuppliersTemplate = (e) => {
    e.stopPropagation()
    e.preventDefault()
    window.open(suppliersTemplate)
  }

  const onLoadArticlesMass = ({ data, fileName }) => {
    setImportData(data)
    setFileName(fileName)
    setViewState(VIEW_STATE.ARTICLES_MASS)
  }

  const onLoadCustomersMass = ({ data, fileName }) => {
    setImportData(data)
    setFileName(fileName)
    setViewState(VIEW_STATE.CUSTOMERS_MASS)
  }

  const onLoadSuppliersMass = ({ data, fileName }) => {
    setImportData(data)
    setFileName(fileName)
    setViewState(VIEW_STATE.SUPPLIERS_MASS)
  }

  let contentArea

  switch (viewState) {
    case VIEW_STATE.EDIT:
      contentArea = (
        <div className={classes.importSelection}>
          { canImportCustomers && (<DataImportInput
            onDownloadTemplate={downloadCustomersTemplate}
            skipTransformation
            dragLabel={lang.massImport2.dragLabel}
            allowedExtensions={['xlsx', 'xls']}
            icon={IconCustomers}
            label={texts.customersMassLabel}
            showDownloadTemplate={false}
            onLoad={onLoadCustomersMass}
            type={IMPORT_TYPE_CUSTOMERS_MASS} />)}
          {canImportSupplier && (<DataImportInput
            onDownloadTemplate={downloadSuppliersTemplate}
            skipTransformation
            dragLabel={lang.massImport2.dragLabel}
            allowedExtensions={['xlsx', 'xls']}
            icon={IconSuppliers}
            label={texts.suppliersMassLabel}
            showDownloadTemplate={false}
            onLoad={onLoadSuppliersMass}
            type={IMPORT_TYPE_SUPPLIERS_MASS} />)}
          {canImportArticles && (<DataImportInput
            onDownloadTemplate={downloadArticlesTemplate}
            skipTransformation
            dragLabel={lang.massImport2.dragLabel}
            allowedExtensions={['xlsx', 'xls']}
            icon={IconArticles}
            label={texts.articlesMassLabel}
            showDownloadTemplate={false}
            onLoad={onLoadArticlesMass}
            type={IMPORT_TYPE_ARTICLES_MASS} />)}
          { !subscriptionsLoading && !canImportArticles && !canImportCustomers && !canImportSupplier && (<div>{lang.massImport.noImportsAvailable}</div>)}
        </div>
      )
      break

    case VIEW_STATE.ARTICLES_MASS:
      contentArea = <MassImportMain importType={IMPORT_TYPE_ARTICLES} data={data} fileName={fileName} abort={onAbortClick} />
      break

    case VIEW_STATE.CUSTOMERS_MASS:
      contentArea = <MassImportMain importType={IMPORT_TYPE_CUSTOMERS} data={data} fileName={fileName} abort={onAbortClick} />
      break

    case VIEW_STATE.SUPPLIERS_MASS:
      contentArea = <MassImportMain importType={IMPORT_TYPE_SUPPLIERS} data={data} fileName={fileName} abort={onAbortClick} />
      break
  }

  const topBar = [
    VIEW_STATE.ARTICLES_MASS,
    VIEW_STATE.CUSTOMERS_MASS,
    VIEW_STATE.SUPPLIERS_MASS,
  ].includes(viewState)
    ? null
    : <Topbar variant='white' title={lang.massImport.topBarTitle}></Topbar>

  return (
    <div data-hook="data-import-overview-amp-wrapper" class="page data-import-overview-amp-wrapper">
      <div className={classes.dataImportComponent}>
        {topBar}
        <div className={classes.dataImportContent}>
          {contentArea}
        </div>
      </div>
    </div>
  )
}

export default withAsyncData(DataImportComponent, lang.massImport.genericTitle)
